import { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, Users, Target, Filter, BarChart3 } from 'lucide-react'
import LoadingSpinner from '../ui/LoadingSpinner'
import ErrorMessage from '../ui/ErrorMessage'
import MetricCard from '../ui/MetricCard'
import DataTable from '../ui/DataTable'
import { apiService } from '../../services/api'
import { debug } from '../../utils/debug'
import { useUser } from '../../context/UserContext'

const VisitEffectivenessDetails = () => {
  const { hasOperatorAccess } = useUser()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [summaryData, setSummaryData] = useState([])
  const [detailedData, setDetailedData] = useState([])
  const [operatorsData, setOperatorsData] = useState([])
  
  // Filters
  const [daysBack, setDaysBack] = useState(30)
  const [operatorFilter, setOperatorFilter] = useState(null)
  const [visitTypesFilter, setVisitTypesFilter] = useState(['Relacyjna', 'Szkoleniowa'])
  const [maturityFilter, setMaturityFilter] = useState(0)
  const [comparisonPeriod, setComparisonPeriod] = useState(7)
  
  // Modal state for KAM details
  const [selectedKam, setSelectedKam] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [kamVisitsData, setKamVisitsData] = useState([])
  
  // Modal state for visit details
  const [selectedVisit, setSelectedVisit] = useState(null)
  const [visitModalOpen, setVisitModalOpen] = useState(false)

  debug.component('VisitEffectivenessDetails', 'Component initialized')

  useEffect(() => {
    fetchOperators()
  }, [])

  useEffect(() => {
    if (operatorsData.length > 0) {
      fetchData()
    }
  }, [daysBack, operatorFilter, visitTypesFilter, maturityFilter, comparisonPeriod, operatorsData])

  const fetchOperators = async () => {
    try {
      const response = await apiService.getVisitOperators()
      setOperatorsData(response.data || [])
      debug.log('Operators data fetched', response.data, 'VisitEffectivenessDetails')
    } catch (err) {
      debug.error('Error fetching operators', err, 'VisitEffectivenessDetails')
    }
  }

  const fetchData = async () => {
    debug.log('Fetching visit effectiveness data', { daysBack, operatorFilter, visitTypesFilter, maturityFilter, comparisonPeriod }, 'VisitEffectivenessDetails')
    console.log('🔄 fetchData called with comparison period:', comparisonPeriod)
    setLoading(true)
    setError(null)

    try {
      const params = {
        days_back: daysBack,
        maturity_filter_days: maturityFilter,
        comparison_period: comparisonPeriod
      }
      if (operatorFilter) {
        params.operator_id = operatorFilter
      }
      if (visitTypesFilter && visitTypesFilter.length > 0) {
        params.visit_type = visitTypesFilter
      }
      

      // Fetch both summary and detailed data
      const [summaryResponse, detailedResponse] = await Promise.all([
        apiService.getVisitEffectivenessSummary(params),
        apiService.getVisitEffectiveness(params)
      ])

      setSummaryData(summaryResponse.data || [])
      setDetailedData(detailedResponse.data || [])
      
      console.log('📊 Summary data received:', summaryResponse.data?.slice(0, 2))
      console.log('📊 Comparison period used in API:', params.comparison_period)
      debug.log('Data fetched successfully', { summary: summaryResponse.data, detailed: detailedResponse.data }, 'VisitEffectivenessDetails')
    } catch (err) {
      debug.error('Error fetching visit effectiveness data', err, 'VisitEffectivenessDetails')
      setError('Nie udało się załadować danych efektywności wizyt.')
    } finally {
      setLoading(false)
    }
  }

  const availableVisitTypes = ["Relacyjna", "Szkoleniowa", "Utrwalająca wiedzę", "Szkolenie online (Dystrykt)", "Szkolenie online (Region)"]

  // Build operator options based on available operators
  const operatorOptions = [
    // Show "Wszystkie" only if user has access to more than one operator
    ...(operatorsData.length > 1 ? [{ id: null, name: "Wszystkie" }] : []),
    ...operatorsData.map(op => ({ id: op.id, name: op.name }))
  ]

  const maturityOptions = [
    { value: 0, label: "Brak filtra (wszystkie)" },
    { value: 7, label: "Min. 7 dni od wizyty" },
    { value: 14, label: "Min. 14 dni (pełna analiza)" }
  ]

  const handleVisitDetailsClick = (visit) => {
    // Find the full visit data from detailedData
    const fullVisit = detailedData.find(v => v.visit_id === visit.visit_id)
    setSelectedVisit(fullVisit || visit)
    setVisitModalOpen(true)
    debug.log('Visit details modal opened', fullVisit || visit, 'VisitEffectivenessDetails')
  }

  const handleKamDetailsClick = async (kam) => {
    setSelectedKam(kam)
    setModalOpen(true)
    
    try {
      // Fetch detailed visits for this KAM and operator
      const params = {
        days_back: daysBack,
        maturity_filter_days: maturityFilter,
        comparison_period: comparisonPeriod
      }
      
      // Add filters if set
      if (kam.operator_id) {
        // Get operator_id from operators data based on operator_name
        const operator = operatorsData.find(op => op.name === kam.operator_name)
        if (operator) {
          params.operator_id = operator.id
        }
      }
      
      if (visitTypesFilter && visitTypesFilter.length > 0) {
        params.visit_type = visitTypesFilter
      }

      console.log('🔍 Modal API params:', params)
      console.log('🔍 Selected KAM:', kam)
      
      const response = await apiService.getVisitEffectiveness(params)
      
      console.log('🔍 Raw API response length:', response.data?.length || 0)
      console.log('🔍 Raw API response first 3:', response.data?.slice(0, 3) || [])
      
      // Filter visits for this specific KAM
      const kamVisits = response.data.filter(visit => 
        visit.kam_name === kam.kam_name && visit.operator_name === kam.operator_name
      )
      
      console.log('🔍 Filtered KAM visits:', kamVisits.length)
      console.log('🔍 First few visits:')
      kamVisits.slice(0, 5).forEach((visit, i) => {
        console.log(`Visit ${i+1}:`, {
          date: visit.visit_date?.substring(0, 10),
          salon: visit.salon_name,
          type: visit.visit_type,
          sales_7d_before: visit.sales_7d_before,
          sales_7d_after: visit.sales_7d_after,
          sales_change_7d: visit.sales_change_7d,
          effectiveness_7d_pct: visit.effectiveness_7d_pct,
          effectiveness_14d_pct: visit.effectiveness_14d_pct,
          effect_type: visit.effect_type
        })
      })
      
      setKamVisitsData(kamVisits)
      debug.log('KAM visits loaded', { kam: kam.kam_name, visits: kamVisits.length }, 'VisitEffectivenessDetails')
    } catch (err) {
      debug.error('Error loading KAM visits', err, 'VisitEffectivenessDetails')
      setKamVisitsData([])
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error} 
        onRetry={fetchData}
        showRetry={true}
      />
    )
  }

  // Calculate metrics for the selected comparison period
  const effectivenessCol = `effectiveness_${comparisonPeriod}d_pct`
  const effectiveCount = detailedData.filter(visit => 
    visit[effectivenessCol] != null && visit[effectivenessCol] > 0
  ).length

  const otherPeriod = comparisonPeriod === 7 ? 14 : 7
  const otherEffectivenessCol = `effectiveness_${otherPeriod}d_pct`
  const otherEffectiveCount = detailedData.filter(visit => 
    visit[otherEffectivenessCol] != null && visit[otherEffectivenessCol] > 0
  ).length

  const avgEffectiveness = detailedData.length > 0 
    ? detailedData.reduce((sum, visit) => sum + (visit[effectivenessCol] || 0), 0) / detailedData.length 
    : 0

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Okres analizy</label>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={7}>7 dni</option>
            <option value={14}>14 dni</option>
            <option value={30}>30 dni</option>
            <option value={60}>60 dni</option>
            <option value={90}>90 dni</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
          <select
            value={operatorFilter || ''}
            onChange={(e) => setOperatorFilter(e.target.value ? Number(e.target.value) : null)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            {operatorOptions.map(option => (
              <option key={option.id || 'all'} value={option.id || ''}>
                {option.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Typy wizyt</label>
          <select
            multiple
            value={visitTypesFilter}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value)
              setVisitTypesFilter(selected)
            }}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            size={3}
          >
            {availableVisitTypes.map(type => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Ctrl+klik dla wielu opcji</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dojrzałość danych</label>
          <select
            value={maturityFilter}
            onChange={(e) => setMaturityFilter(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            {maturityOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Okres porównania</label>
          <select
            value={comparisonPeriod}
            onChange={(e) => {
              const newPeriod = Number(e.target.value)
              console.log('🔄 Comparison period changed from', comparisonPeriod, 'to', newPeriod)
              setComparisonPeriod(newPeriod)
            }}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={7}>7 dni</option>
            <option value={14}>14 dni</option>
          </select>
        </div>
      </div>

      {/* Summary per KAM */}
      {summaryData.length > 0 && (
        <div className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">📊 Podsumowanie Efektywności per KAM ({comparisonPeriod}d)</h3>
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Wzrost:</strong> Liczba wizyt z efektywnością ≥25% (przed/po porównaniu w {comparisonPeriod}d).
                <br />
                <strong>Spadek:</strong> Liczba wizyt z efektywnością ≤-25% (spadek sprzedaży po wizycie).
                <br />
                <strong>Łączny Wzrost:</strong> Suma zmian sprzedaży ze wszystkich wizyt KAM-a (może być ujemna).
              </p>
            </div>
          </div>
          
          <DataTable
            rows={summaryData.map((item, index) => ({
              id: index,
              kam_name: item.kam_name || '',
              operator_name: item.operator_name || '',
              total_visits: Number(item.total_visits) || 0,
              growth_visits_count: Number(item.growth_visits_count) || 0,
              decline_visits_count: Number(item.decline_visits_count) || 0,
              total_sales_change: comparisonPeriod === 7 ? (Number(item.total_sales_change_7d) || 0) : (Number(item.total_sales_change_14d) || 0),
            }))}
            columns={[
              {
                field: 'kam_name',
                headerName: 'KAM',
                flex: 2,
                minWidth: 120,
                type: 'string',
              },
              {
                field: 'operator_name',
                headerName: 'Operator',
                flex: 1.5,
                minWidth: 100,
                type: 'string',
              },
              {
                field: 'total_visits',
                headerName: 'Wizyty Ogółem',
                flex: 1.3,
                minWidth: 110,
                type: 'number',
              },
              {
                field: 'growth_visits_count',
                headerName: 'Wzrost',
                flex: 1,
                minWidth: 90,
                type: 'number',
                renderCell: (params) => (
                  <div className="flex items-center justify-end">
                    <TrendingUp className="h-4 w-4 text-green-600 mr-1" />
                    <span className="text-green-700 font-medium">{params.value}</span>
                  </div>
                ),
              },
              {
                field: 'decline_visits_count',
                headerName: 'Spadek',
                flex: 1,
                minWidth: 90,
                type: 'number',
                renderCell: (params) => (
                  <div className="flex items-center justify-end">
                    <TrendingDown className="h-4 w-4 text-red-600 mr-1" />
                    <span className="text-red-700 font-medium">{params.value}</span>
                  </div>
                ),
              },
              {
                field: 'total_sales_change',
                headerName: `Łączny Wzrost ${comparisonPeriod}d`,
                flex: 1.6,
                minWidth: 140,
                type: 'number',
                renderCell: (params) => <span>{(params.value ?? 0) > 0 ? `+${params.value ?? 0}` : String(params.value ?? 0)}</span>,
              },
              {
                field: 'actions',
                headerName: 'Akcje',
                flex: 1,
                minWidth: 100,
                sortable: false,
                renderCell: (params) => (
                  <button
                    onClick={() => handleKamDetailsClick(params.row)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    Szczegóły
                  </button>
                ),
              },
            ]}
            pageSize={10}
            autoHeight
            sortModel={[{ field: 'total_visits', sort: 'desc' }]}
          />
        </div>
      )}

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Wizyty Ogółem"
          value={detailedData.length}
          icon={Users}
        />
        <MetricCard
          title={`Skuteczne ${comparisonPeriod}d`}
          value={effectiveCount}
          icon={TrendingUp}
        />
        <MetricCard
          title={`Skuteczne ${otherPeriod}d`}
          value={otherEffectiveCount}
          icon={Target}
          subtitle="Dla porównania"
        />
        <MetricCard
          title={`Śr. Efektywność ${comparisonPeriod}d`}
          value={`${avgEffectiveness.toFixed(1)}%`}
          icon={BarChart3}
        />
      </div>

      {/* Detailed Visits Table */}
      {detailedData.length > 0 && (
        <div className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">🔍 Szczegółowe Dane Efektywności ({comparisonPeriod}d)</h3>
            <div className="mt-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-800">
                <strong>Sprzedaż Przed/Po:</strong> Porównanie sprzedaży {comparisonPeriod} dni przed wizytą vs {comparisonPeriod} dni po wizycie.
                <br />
                <strong>Zmiana:</strong> Różnica w sprzedaży (po - przed). Dodatnia = wzrost sprzedaży po wizycie.
                <br />
                <strong>Efekt. %:</strong> Procentowa zmiana sprzedaży. ≥25% = wizyta skuteczna (wzrost).
              </p>
            </div>
          </div>
          
          <DataTable
            rows={detailedData.map((visit, index) => {
              const salesBefore = Number(visit[`sales_${comparisonPeriod}d_before`]) || 0
              const salesAfter = Number(visit[`sales_${comparisonPeriod}d_after`]) || 0
              const salesChange = Number(visit[`sales_change_${comparisonPeriod}d`]) || 0
              const effectiveness = Number(visit[effectivenessCol]) || 0
              
              const effectType = visit.effect_type === 'growth' ? '📈 Wzrost' 
                              : ''

              return {
                id: index,
                visit_id: visit.visit_id || index,
                visit_date: visit.visit_date?.substring(0, 10) || '—',
                kam_name: visit.kam_name || '',
                salon_name: visit.salon_name || '',
                operator_name: visit.operator_name || '',
                visit_type: visit.visit_type || '',
                sales_before: salesBefore,
                sales_after: salesAfter,
                sales_change: salesChange,
                effectiveness: effectiveness,
                effect_type: effectType,
              }
            })}
            columns={[
              {
                field: 'visit_date',
                headerName: 'Data Wizyty',
                flex: 1,
                minWidth: 100,
                type: 'string',
              },
              {
                field: 'kam_name',
                headerName: 'KAM',
                flex: 1.3,
                minWidth: 110,
                type: 'string',
              },
              {
                field: 'salon_name',
                headerName: 'Salon',
                flex: 2,
                minWidth: 150,
                type: 'string',
              },
              {
                field: 'operator_name',
                headerName: 'Operator',
                flex: 1,
                minWidth: 90,
                type: 'string',
              },
              {
                field: 'visit_type',
                headerName: 'Typ',
                flex: 1.2,
                minWidth: 100,
                type: 'string',
              },
              {
                field: 'sales_before',
                headerName: `Sprzedaż ${comparisonPeriod}d Przed`,
                flex: 1.4,
                minWidth: 120,
                type: 'number',
              },
              {
                field: 'sales_after',
                headerName: `Sprzedaż ${comparisonPeriod}d Po`,
                flex: 1.3,
                minWidth: 110,
                type: 'number',
              },
              {
                field: 'sales_change',
                headerName: `Zmiana ${comparisonPeriod}d`,
                flex: 1.2,
                minWidth: 100,
                type: 'number',
                renderCell: (params) => <span>{(params.value ?? 0) > 0 ? `+${params.value ?? 0}` : String(params.value ?? 0)}</span>,
              },
              {
                field: 'effectiveness',
                headerName: `Efekt. ${comparisonPeriod}d (%)`,
                flex: 1.3,
                minWidth: 110,
                type: 'number',
                renderCell: (params) => <span>{(params.value ?? 0).toFixed(1)}%</span>,
              },
              {
                field: 'effect_type',
                headerName: 'Typ Efektu',
                flex: 1.1,
                minWidth: 100,
                type: 'string',
                sortable: false,
              },
              {
                field: 'actions',
                headerName: 'Akcje',
                flex: 1,
                minWidth: 100,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                  <button
                    onClick={() => handleVisitDetailsClick(params.row)}
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    Szczegóły
                  </button>
                ),
              },
            ]}
            pageSize={25}
            height={600}
            sortModel={[{ field: 'effectiveness', sort: 'desc' }]}
            onRowClick={(params) => handleVisitSelect(params.row.visit_id)}
          />
        </div>
      )}

      {/* Effectiveness Distribution Chart */}
      {detailedData.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">📊 Jak Skuteczne Są Nasze Wizyty? ({comparisonPeriod}d)</h3>
          
          {/* Summary metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="text-2xl">🟢</div>
                <div>
                  <div className="text-lg font-semibold text-green-800">
                    {detailedData.filter(v => (v[effectivenessCol] || 0) > 0).length}
                  </div>
                  <div className="text-sm text-green-600">Pozytywne Wizyty</div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="text-2xl">🔴</div>
                <div>
                  <div className="text-lg font-semibold text-red-800">
                    {detailedData.filter(v => (v[effectivenessCol] || 0) < 0).length}
                  </div>
                  <div className="text-sm text-red-600">Wizyty ze Spadkiem</div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="text-2xl">📈</div>
                <div>
                  <div className="text-lg font-semibold text-blue-800">
                    {detailedData.length > 0 
                      ? ((detailedData.filter(v => (v[effectivenessCol] || 0) > 0).length / detailedData.length) * 100).toFixed(1)
                      : 0}%
                  </div>
                  <div className="text-sm text-blue-600">Wskaźnik Sukcesu</div>
                </div>
              </div>
            </div>
          </div>

          {/* Simple bar chart representation */}
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="space-y-3">
              {[
                { label: 'Spadki', min: -Infinity, max: 0, color: 'bg-red-500' },
                { label: '0-10%', min: 0, max: 10, color: 'bg-yellow-500' },
                { label: '10-20%', min: 10, max: 20, color: 'bg-blue-500' },
                { label: '20-50%', min: 20, max: 50, color: 'bg-green-500' },
                { label: '50-100%', min: 50, max: 100, color: 'bg-green-600' },
                { label: '100%+', min: 100, max: Infinity, color: 'bg-green-700' },
              ].map(bucket => {
                const count = detailedData.filter(v => {
                  const eff = v[effectivenessCol] || 0
                  return eff >= bucket.min && (bucket.max === Infinity ? true : eff < bucket.max)
                }).length
                const percentage = detailedData.length > 0 ? (count / detailedData.length) * 100 : 0

                return (
                  <div key={bucket.label} className="flex items-center space-x-4">
                    <div className="w-16 text-sm text-gray-600">{bucket.label}</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                      <div
                        className={`h-6 rounded-full ${bucket.color}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <div className="w-16 text-sm text-gray-900 text-right">{count}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}


      {/* KAM Details Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Szczegóły wizyt: {selectedKam?.kam_name} - {selectedKam?.operator_name}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Zamknij</span>
                ✕
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {kamVisitsData.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Łączna liczba wizyt:</span>
                        <div className="text-lg font-semibold text-blue-600">{kamVisitsData.length}</div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Wzrost (≥25%) {comparisonPeriod}d:</span>
                        <div className="text-lg font-semibold text-green-600">
                          {kamVisitsData.filter(v => {
                            const effectiveness = comparisonPeriod === 7 ? v.effectiveness_7d_pct : v.effectiveness_14d_pct
                            return effectiveness >= 25
                          }).length}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Spadek (≤-25%) {comparisonPeriod}d:</span>
                        <div className="text-lg font-semibold text-red-600">
                          {kamVisitsData.filter(v => {
                            const effectiveness = comparisonPeriod === 7 ? v.effectiveness_7d_pct : v.effectiveness_14d_pct
                            return effectiveness <= -25
                          }).length}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Łączny wzrost {comparisonPeriod}d:</span>
                        <div className="text-lg font-semibold text-purple-600">
                          {(() => {
                            const salesChangeField = `sales_change_${comparisonPeriod}d`
                            const total = kamVisitsData.reduce((sum, v) => sum + (v[salesChangeField] || 0), 0)
                            return `${total > 0 ? '+' : ''}${total}`
                          })()}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Łączny wzrost {comparisonPeriod === 7 ? '14d' : '7d'}:</span>
                        <div className="text-lg font-semibold text-orange-600">
                          {(() => {
                            const otherPeriod = comparisonPeriod === 7 ? 14 : 7
                            const salesChangeField = `sales_change_${otherPeriod}d`
                            const total = kamVisitsData.reduce((sum, v) => sum + (v[salesChangeField] || 0), 0)
                            return `${total > 0 ? '+' : ''}${total}`
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data wizyty</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salon</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Przed 7d</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Po 7d</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Zmiana 7d</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Efekt. 7d (%)</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Efekt. 14d (%)</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {kamVisitsData.map((visit, index) => (
                          <tr key={visit.visit_id || index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {visit.visit_date?.substring(0, 10) || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {visit.salon_name || `Salon ${visit.salon_id}`}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {visit.visit_type || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {visit.sales_7d_before || 0}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900">
                              {visit.sales_7d_after || 0}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              <span className={`${(visit.sales_change_7d || 0) > 0 ? 'text-green-600' : 
                                (visit.sales_change_7d || 0) < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {(visit.sales_change_7d || 0) > 0 ? '+' : ''}{visit.sales_change_7d || 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              <span className={`${(visit.effectiveness_7d_pct || 0) >= 25 ? 'text-green-600' : 'text-gray-600'}`}>
                                {(visit.effectiveness_7d_pct || 0).toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              <span className={`${(visit.effectiveness_14d_pct || 0) >= 25 ? 'text-green-600' : 'text-gray-600'}`}>
                                {(visit.effectiveness_14d_pct || 0).toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {visit.effect_type === 'growth' || 
                               (visit.effectiveness_7d_pct >= 25 || visit.effectiveness_14d_pct >= 25) ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  📈 Wzrost
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Brak danych wizyt dla wybranego KAM-a i operatora
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visit Details Modal */}
      {visitModalOpen && selectedVisit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Szczegóły wizyty: {selectedVisit.salon_name}
              </h3>
              <button
                onClick={() => setVisitModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Zamknij</span>
                ✕
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Informacje podstawowe</h4>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Data wizyty:</span>
                      <span className="font-medium">{selectedVisit.visit_date?.substring(0, 10) || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">KAM:</span>
                      <span className="font-medium">{selectedVisit.kam_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Salon:</span>
                      <span className="font-medium">{selectedVisit.salon_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Operator:</span>
                      <span className="font-medium">{selectedVisit.operator_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Typ wizyty:</span>
                      <span className="font-medium">{selectedVisit.visit_type}</span>
                    </div>
                  </div>
                </div>

                {/* Sales Data 7d */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Dane sprzedażowe 7 dni</h4>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sprzedaż przed wizytą:</span>
                      <span className="font-medium">{selectedVisit.sales_7d_before || 0} szt.</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sprzedaż po wizycie:</span>
                      <span className="font-medium">{selectedVisit.sales_7d_after || 0} szt.</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Zmiana:</span>
                      <span className={`font-semibold ${
                        (selectedVisit.sales_change_7d || 0) > 0 ? 'text-green-600' : 
                        (selectedVisit.sales_change_7d || 0) < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {(selectedVisit.sales_change_7d || 0) > 0 ? '+' : ''}{selectedVisit.sales_change_7d || 0} szt.
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Efektywność:</span>
                      <span className={`font-semibold ${
                        (selectedVisit.effectiveness_7d_pct || 0) >= 25 ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {(selectedVisit.effectiveness_7d_pct || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sales Data 14d */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Dane sprzedażowe 14 dni</h4>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sprzedaż przed wizytą:</span>
                      <span className="font-medium">{selectedVisit.sales_14d_before || 0} szt.</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sprzedaż po wizycie:</span>
                      <span className="font-medium">{selectedVisit.sales_14d_after || 0} szt.</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Zmiana:</span>
                      <span className={`font-semibold ${
                        (selectedVisit.sales_change_14d || 0) > 0 ? 'text-green-600' : 
                        (selectedVisit.sales_change_14d || 0) < 0 ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {(selectedVisit.sales_change_14d || 0) > 0 ? '+' : ''}{selectedVisit.sales_change_14d || 0} szt.
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Efektywność:</span>
                      <span className={`font-semibold ${
                        (selectedVisit.effectiveness_14d_pct || 0) >= 25 ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {(selectedVisit.effectiveness_14d_pct || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status & Summary */}
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-800 border-b pb-2">Podsumowanie</h4>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status efektywności:</span>
                      <span>
                        {((selectedVisit.effectiveness_7d_pct >= 25) || (selectedVisit.effectiveness_14d_pct >= 25)) ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            📈 Wzrost
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            — Brak wzrostu
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">ID wizyty:</span>
                      <span className="font-mono text-sm">{selectedVisit.visit_id}</span>
                    </div>
                    {selectedVisit.salon_id && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">ID salonu:</span>
                        <span className="font-mono text-sm">{selectedVisit.salon_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
              <button
                onClick={() => setVisitModalOpen(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VisitEffectivenessDetails
